import type { ExternalBoard, ExternalProject } from '../projects-client/projects-client.type';

enum IntegrationAuthType {
  Oauth = 'oauth',
  Basic = 'basic',
  Token = 'token',
}

type ADOProject = {
  id: string;
  name: string;
  description: string;
  lastUpdatedTime: string;
  organizationId: string;
  organization_name: string;
  revision: number;
  state: string;
  url: string;
  visibility: string;
};

type ADOTeam = {
  id: string;
  name: string;
  description: string;
  identityUrl: string;
  organizationName: string;
  projectId: string;
  projectName: string;
  url: string;
};

type Integration = {
  auth_type: IntegrationAuthType;
  auth_url: string;
  description: string;
  id: string;
  instructions: string;
  logo: string;
  name: string;
  system_type: string;
  enabled?: boolean;
};

enum ExternalService {
  Jira = 'jira',
  JDC = 'jdc',
  Github = 'github',
  GithubActions = 'github_actions',
  Figma = 'figma',
  ADO = 'ado',
  Gitlab = 'gitlab',
  Confluence = 'confluence',
  Aha = 'aha',
  Bitbucket = 'bitbucket',
  Notion = 'notion',
  Asana = 'asana',
  ADO_Repos = 'ado_repository',
  Linear = 'linear',
}

enum Methodology {
  Scrum = 'scrum',
  Kanban = 'kanban',
}

type SetStatusMappingPayload = {
  service_name: string;
  project: ExternalProject | ADOProject | IntegrationJDC;
  board: ExternalBoard | ADOTeam | BoardJDC;
  org_id: string;
  backlog_section: string[];
  blocked_section: string[];
  in_progress_section: string[];
  in_review_section: string[];
  in_test_section: string[];
  ready_for_deployment_section: string[];
  done_section: string[];
  project_url?: string;
  configuration_state?: string;
};

enum IntegrationType {
  DUMMY = 'dummy',
  BLOOMFILTER = 'bloomfilter',
  ADO = 'ado',
  GITHUB = 'github',
  JIRA = 'jira',
  JDC = 'jira_data_center',
  FIGMA = 'figma',
  LINEAR = 'linear',
}

type IntegrationEntry = {
  id: string;
  integration_type: IntegrationType;
  user_id: string;
  user_name: string;
  projects: Project[];
  subprojects: Subproject[];
  repositories?: IntegrationRepository[];
  last_completed_at: string | null;
  last_run_status: string | null;
  manually_prioritized: boolean;
  current_stage: CurrentStage;
  health_issue_count?: number;
};

type Project = {
  id: string;
  name: string;
  last_completed_at: string | null;
  subproject_ids: string[];
};

type IntegrationRepository = {
  id: string;
  name: string;
  project_id: string | null;
  project_name: string;
  project_last_completed_at: string | null;
  last_data_fetch: string | null;
  configuration_state: string;
  archived: boolean;
};

type Subproject = {
  id: string;
  name: string | null;
  project_id: string;
  project_name: string | null;
  project_pm_tool?: string;
  configuration_state?: string;
  last_data_fetch: string;
  subproject_settings_id?: string;
  subproject_settings_name?: string;
};

enum CurrentStage {
  UNKNOWN = 'UNKNOWN',
  WAITING = 'WAITING',
  RUNNING = 'RUNNING',
  COMPLETE = 'COMPLETE',
  INTERRUPT = 'INTERRUPT',
  FAILED = 'FAILED',
}

type IntegrationRefreshData = {
  message: string;
  status: number;
};

type IntegrationJDC = {
  name: string;
  id: number;
  internal_id: string;
  key: string;
  boards: BoardJDC[];
};

type BoardJDC = {
  name: string;
  id: number;
  internal_id: string;
};

type StartIntegrationProcessPayload = {
  organization_id: string;
  service: ExternalService;
  state: string;
  system_access_id: string;
  integration_id: string | null;
};

export { CurrentStage, ExternalService, IntegrationAuthType, IntegrationType, Methodology };
export type {
  ADOProject,
  ADOTeam,
  BoardJDC,
  Integration,
  IntegrationEntry,
  IntegrationJDC,
  IntegrationRefreshData,
  SetStatusMappingPayload,
  StartIntegrationProcessPayload,
  Subproject,
};
